import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { parsePhoneNumber } from 'libphonenumber-js';
import { CountrySelector, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { phoneCountries } from '@/lib/phone';

import type { TelefonoContactoFormRow } from '../schemas';

type TelefonosFormContext = {
  telefonos_contacto: TelefonoContactoFormRow[];
};

type TelefonoRowError = {
  numero?: { message?: string };
  nombre?: { message?: string };
};

interface PhoneInputProps {
  value: string;
  onChange: (e164: string) => void;
  onBlur: () => void;
  error?: string;
  disabled?: boolean;
}

function PhoneInput({ value, onChange, onBlur, error, disabled }: PhoneInputProps): JSX.Element {
  const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry: 'ar',
    value,
    countries: phoneCountries,
    onChange: ({ phone }) => {
      try {
        const parsed = phone ? parsePhoneNumber(phone) : null;
        onChange(parsed?.nationalNumber ? phone : '');
      } catch {
        onChange('');
      }
    },
  });

  return (
    <TextField
      fullWidth
      size="small"
      label="Número"
      value={inputValue}
      onChange={handlePhoneValueChange}
      onBlur={onBlur}
      inputRef={inputRef}
      disabled={disabled}
      error={!!error}
      helperText={error ?? ' '}
      InputProps={{
        startAdornment: (
          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
            <CountrySelector
              selectedCountry={country.iso2}
              onSelect={({ iso2 }) => setCountry(iso2)}
              disabled={disabled}
              countries={phoneCountries}
            />
          </Box>
        ),
      }}
    />
  );
}

interface TelefonosContactoSectionProps {
  disabled?: boolean;
}

export function TelefonosContactoSection({ disabled }: TelefonosContactoSectionProps): JSX.Element {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<TelefonosFormContext>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'telefonos_contacto',
  });

  const telefonosErrors =
    (errors.telefonos_contacto as TelefonoRowError[] | undefined) ?? [];

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
      <Stack direction="row" alignItems="baseline" spacing={1} mb={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Teléfonos de contacto
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Opcional
        </Typography>
      </Stack>

      {fields.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No hay teléfonos de contacto cargados.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {fields.map((field, index) => {
            const rowError = telefonosErrors[index];
            return (
              <Box
                key={field.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr auto', sm: '2fr 1fr auto' },
                  gridTemplateRows: { xs: 'auto auto', sm: 'auto' },
                  gap: 1,
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ gridColumn: { xs: '1', sm: '1' } }}>
                  <Controller
                    control={control}
                    name={`telefonos_contacto.${index}.numero` as never}
                    render={({ field: phoneField }) => (
                      <PhoneInput
                        value={(phoneField.value as string) ?? ''}
                        onChange={phoneField.onChange}
                        onBlur={phoneField.onBlur}
                        error={rowError?.numero?.message}
                        disabled={disabled}
                      />
                    )}
                  />
                </Box>
                <TextField
                  label="Nombre (opcional)"
                  size="small"
                  fullWidth
                  {...register(`telefonos_contacto.${index}.nombre`)}
                  error={!!rowError?.nombre}
                  helperText={rowError?.nombre?.message ?? ' '}
                  disabled={disabled}
                  sx={{ gridColumn: { xs: '1 / -1', sm: '2' } }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => remove(index)}
                  disabled={disabled}
                  aria-label="Eliminar teléfono"
                  sx={{
                    mt: 0.5,
                    flexShrink: 0,
                    gridColumn: { xs: '2', sm: '3' },
                    gridRow: { xs: '1', sm: '1' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      )}

      <Button
        size="small"
        onClick={() => append({ numero: '', nombre: '' })}
        disabled={disabled}
      >
        + Agregar teléfono
      </Button>
    </Paper>
  );
}
